'use client';

/**
 * Faces d'une SKILLCARD (carte à jouer 63 × 88 mm) — recto (l'outil) et verso (carte à collectionner :
 * créature origami, teinte du niveau, rareté, QR).
 *
 * Rendues sur un gabarit 74 × 103,37 mm (rapport 63:88, cf. card-face.css) : la même face sert
 * à l'aperçu de l'éditeur (via `ScaledCard`), à l'impression et au retournement
 * 3D de la page publique — un seul rendu, aucune divergence possible. Partagé
 * par l'app ET le site marketing (galerie `/cartes-skills`) via
 * `@jeraisonne/ui/tool-cards`.
 */

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { CompassLogo } from '../compass-logo';
import { MarkdownMath } from '../content/markdown-math';
import { MathText } from '../content/math-text';
import { VisualBlockRenderer } from '../content/visual-blocks/visual-block-renderer';
import type { ExerciseVisualBlock } from '@jeraisonne/ai/visual-blocks';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import {
  CARD_RARITY_LABELS,
  CARD_RARITY_STARS,
  CARD_TYPE_LABELS,
  CARD_QR_OPTIONS,
  buildCardShortUrl,
  buildCardQrUrl,
  cardPalette,
  staticCardVisualBlock,
  stripMath,
  type CardColorTheme,
  type CardContent,
  type CardRarity,
  type CardSection,
  type CardType,
} from '@jeraisonne/db/cards';
import { QrCodeImage } from './qr-code-image';
import { FitWidth } from './fit-width';
import { OrigamiCreature } from './origami-creature';
import {
  cardEmblem,
  cardHashtag,
  cardLevelRange,
  cardXpGroups,
  formatCardXp,
  versoTitleSize,
} from './verso-stats';
import './card-face.css';

const LEVEL_LABELS: Record<string, string> = Object.fromEntries(
  SCHOOL_LEVELS.map((l) => [l.value, l.label]),
);

/** « 3e · 2nde » — niveaux dans l'ordre scolaire. */
export function formatCardLevels(levels: readonly string[]): string {
  const order = SCHOOL_LEVELS.map((l) => l.value as string);
  return [...levels]
    .sort((a, b) => order.indexOf(a) - order.indexOf(b))
    .map((l) => LEVEL_LABELS[l] ?? l)
    .join(' · ');
}

/** Un geste sur une figure interactive ne retourne pas la carte (ni ne ferme la lecture). */
function stopFlip(e: { stopPropagation: () => void }) {
  e.stopPropagation();
}

function SectionTitle({ children }: { children?: string }) {
  if (!children) return null;
  return <p className="jr-tool-card-section-title">{children}</p>;
}

function Section({
  section,
  interactiveVisuals = false,
}: {
  section: CardSection;
  interactiveVisuals?: boolean;
}): ReactNode {
  switch (section.kind) {
    case 'steps':
      return (
        <div>
          <SectionTitle>{section.title}</SectionTitle>
          <ol className="jr-tool-card-steps">
            {section.items.map((item, i) => (
              <li key={i}>
                <MarkdownMath>{item}</MarkdownMath>
              </li>
            ))}
          </ol>
        </div>
      );
    case 'formula':
      return (
        <div>
          <SectionTitle>{section.title}</SectionTitle>
          <div className="jr-tool-card-formula">
            <FitWidth>
              <MarkdownMath>{`$$${section.latex}$$`}</MarkdownMath>
            </FitWidth>
            {section.variables.length > 0 && (
              <dl className="jr-tool-card-variables">
                {section.variables.map((v, i) => (
                  <div key={i} className="contents">
                    <dt>
                      <MarkdownMath>{`$${v.symbol}$`}</MarkdownMath>
                    </dt>
                    <dd className="m-0">
                      <MarkdownMath>{v.meaning}</MarkdownMath>
                    </dd>
                  </div>
                ))}
              </dl>
            )}
            {section.conditions && (
              <div className="jr-tool-card-conditions">
                <MarkdownMath>{section.conditions}</MarkdownMath>
              </div>
            )}
          </div>
        </div>
      );
    case 'table':
      return (
        <div>
          <SectionTitle>{section.title}</SectionTitle>
          <table className="jr-tool-card-table">
            <thead>
              <tr>
                {section.headers.map((h, i) => (
                  <th key={i}>
                    <MarkdownMath>{h}</MarkdownMath>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci}>
                      <MarkdownMath>{cell}</MarkdownMath>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'markdown':
      return (
        <div>
          <SectionTitle>{section.title}</SectionTitle>
          <MarkdownMath>{section.md}</MarkdownMath>
        </div>
      );
    case 'example':
      return (
        <div className="jr-tool-card-box jr-tool-card-example">
          <span className="jr-tool-card-box-label">Exemple</span>
          <MarkdownMath>{section.statement}</MarkdownMath>
          {section.steps.map((s, i) => (
            <MarkdownMath key={i}>{s}</MarkdownMath>
          ))}
          {section.result && (
            <div className="jr-tool-card-example-result">
              <MarkdownMath>{`→ ${section.result}`}</MarkdownMath>
            </div>
          )}
        </div>
      );
    case 'when_to_use':
      return (
        <div className="jr-tool-card-box jr-tool-card-when">
          <span className="jr-tool-card-box-label">Quand l’utiliser ?</span>
          <MarkdownMath>{section.md}</MarkdownMath>
        </div>
      );
    case 'pitfall':
      return (
        <div className="jr-tool-card-box jr-tool-card-pitfall">
          <span className="jr-tool-card-box-label">⚠ Attention</span>
          <MarkdownMath>{section.md}</MarkdownMath>
        </div>
      );
    case 'visual':
      return (
        <div>
          {interactiveVisuals ? (
            // Figure manipulable À L'ÉCRAN : ses touchers (zoom, points, curseur)
            // passent AVANT le retournement de la carte — ils ne remontent pas.
            <div
              className="jr-tool-card-visual"
              data-card-interactive=""
              onClick={stopFlip}
              onPointerDown={stopFlip}
              onKeyDown={stopFlip}
            >
              <VisualBlockRenderer block={section.block as unknown as ExerciseVisualBlock} />
            </div>
          ) : (
            <div className="jr-tool-card-visual">
              {/* Figée (impression, aperçus) : ni glisser, ni curseur. */}
              <VisualBlockRenderer
                block={staticCardVisualBlock(section.block) as unknown as ExerciseVisualBlock}
              />
            </div>
          )}
          {section.caption && <p className="jr-tool-card-caption">{section.caption}</p>}
        </div>
      );
  }
}

/**
 * Variables CSS de la teinte (bordure commune recto/verso, puces, formule, QR).
 * Posées sur les DEUX faces : une même carte a forcément la même bordure.
 */
function paletteStyle(levels: readonly string[], colorTheme?: CardColorTheme): CSSProperties {
  const p = cardPalette(levels, colorTheme);
  return { '--jr-card-accent': p.accent, '--jr-card-bg': p.bg } as CSSProperties;
}

interface CardFrameProps {
  /** Niveaux de la carte : la teinte est celle du plus bas (sinon bleu nuit). */
  levels?: readonly string[];
  colorTheme?: CardColorTheme;
  /** Forme évoluée (XP max atteinte) : bordure lumineuse. */
  evolved?: boolean;
}

export interface CardRectoProps extends CardFrameProps {
  title: string;
  cardType: CardType;
  content: CardContent;
  /** Carte allongée à la hauteur de son contenu (lecture plein écran). */
  expanded?: boolean;
  /**
   * Figures manipulables à l'écran (zoom, points, curseur), prioritaires sur le
   * retournement. Faux par défaut : figure figée (impression, aperçus).
   */
  interactiveVisuals?: boolean;
}

/**
 * Suit le débordement du corps du recto : `overflowing` = contenu plus haut
 * que la carte. Le corps ne défile JAMAIS (pas de défilement imbriqué dans une
 * page qui défile, surtout au doigt) : la suite s'ouvre en plein écran.
 */
function useBodyOverflow(enabled: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    const update = () => setOverflowing(el.scrollHeight - el.clientHeight > 1);
    update();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    ro?.observe(el);
    // Le contenu (KaTeX, visuels) peut grandir après le 1er rendu.
    const mo = typeof MutationObserver === 'undefined' ? null : new MutationObserver(update);
    mo?.observe(el, { childList: true, subtree: true });
    return () => {
      ro?.disconnect();
      mo?.disconnect();
    };
  }, [enabled]);

  return { ref, overflowing };
}

/**
 * Lecture de la carte ENTIÈRE en plein écran : la carte s'allonge à la hauteur
 * de son contenu et seule cette fenêtre défile (la page derrière est bloquée).
 * Rendue dans un portail ; les événements ne remontent pas jusqu'à la carte
 * d'origine (qui se retournerait au clic).
 */
export function CardRectoReader({
  onClose,
  ...recto
}: Omit<CardRectoProps, 'expanded'> & { onClose: () => void }) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    rootRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (typeof document === 'undefined') return null;
  return createPortal(
    // Un toucher n'importe où (carte comprise) referme : pas de croix. Le
    // défilement, lui, n'est pas un clic.
    <div
      ref={rootRef}
      tabIndex={-1}
      className="jr-tool-card-reader"
      role="dialog"
      aria-modal="true"
      aria-label={`Carte ${stripMath(recto.title)} en entier — touche pour fermer`}
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div className="jr-tool-card-reader-inner">
        <ScaledCard autoHeight>
          <CardRecto {...recto} expanded />
        </ScaledCard>
      </div>
    </div>,
    document.body,
  );
}

export function CardRecto({
  title,
  cardType,
  content,
  levels = [],
  colorTheme,
  evolved = false,
  expanded = false,
  interactiveVisuals = false,
}: CardRectoProps) {
  const body = useBodyOverflow(!expanded);
  const [reading, setReading] = useState(false);
  const showMore = !expanded && body.overflowing;

  return (
    <article
      className="jr-tool-card jr-tool-card-frame jr-tool-card-recto"
      aria-label={`Recto de la carte ${stripMath(title)}`}
      data-overflowing={showMore || undefined}
      data-expanded={expanded || undefined}
      data-evolved={evolved || undefined}
      style={paletteStyle(levels, colorTheme)}
    >
      <span className="jr-tool-card-type">{CARD_TYPE_LABELS[cardType] ?? cardType}</span>
      <h2 className="jr-tool-card-title">
        <MathText>{title}</MathText>
      </h2>
      <div ref={body.ref} className="jr-tool-card-body" data-fade={showMore || undefined}>
        {content.sections.length === 0 ? (
          <p className="jr-tool-card-empty">Recto vide — génère le contenu avec l’IA.</p>
        ) : (
          content.sections.map((section, i) => (
            <Section key={i} section={section} interactiveVisuals={interactiveVisuals} />
          ))
        )}
      </div>
      {showMore && (
        <button
          type="button"
          className="jr-tool-card-more"
          aria-label="Voir la carte en entier"
          onClick={(e) => {
            e.stopPropagation();
            setReading(true);
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <ChevronDown aria-hidden />
        </button>
      )}
      {showMore && (
        <p className="jr-tool-card-more-print">Suite en ligne : scanne le QR au verso ↻</p>
      )}
      {reading && (
        <CardRectoReader
          title={title}
          cardType={cardType}
          content={content}
          levels={levels}
          colorTheme={colorTheme}
          evolved={evolved}
          onClose={() => setReading(false)}
        />
      )}
    </article>
  );
}

export interface CardVersoProps extends CardFrameProps {
  cardId: string;
  shortCode: string;
  title: string;
  cardType: CardType;
  levels: readonly string[];
  xpMax: number;
  /** Rareté : 1 (simple) à 5 (légendaire) étoiles. */
  rarity: CardRarity;
  domainLabel?: string | null;
  /** Spec de la créature (`cards.creature`) ; absente → créature par défaut. */
  creature?: unknown;
  /** Recto : source de l'élément emblématique (formule, sinon résumé). */
  content?: CardContent | null;
  /** Repli du résumé si le recto n'en fournit pas (règle ou méthode saisie). */
  summaryFallback?: string;
}

/**
 * Verso façon carte à collectionner, dans la teinte du niveau : créature
 * origami en haut à gauche, niveau min → max au-dessus du badge « HORA »
 * (ou « ✦ ÉVOLUÉ »), puces type/domaine, titre, élément emblématique (formule
 * ou résumé), QR sur tuile blanche, rareté en étoiles.
 */
export function CardVerso({
  cardId,
  shortCode,
  title,
  cardType,
  levels,
  xpMax,
  rarity,
  colorTheme,
  evolved = false,
  domainLabel,
  creature,
  content,
  summaryFallback = '',
}: CardVersoProps) {
  const range = cardLevelRange(levels);
  const emblem = cardEmblem(content, summaryFallback);
  const palette = cardPalette(levels, colorTheme);
  const stars = CARD_RARITY_STARS[rarity] ?? 2;
  const rarityLabel = CARD_RARITY_LABELS[rarity] ?? rarity;
  return (
    <article
      className="jr-tool-card jr-tool-card-frame jr-tool-card-verso"
      aria-label={`Verso de la carte ${stripMath(title)}`}
      data-evolved={evolved || undefined}
      data-palette={palette.key}
      style={paletteStyle(levels, colorTheme)}
    >
      <header className="jr-tool-card-verso-top">
        <OrigamiCreature
          creature={creature}
          accent={palette.accent}
          evolved={evolved}
          className="jr-tool-card-creature"
        />
        <span className="jr-tool-card-verso-stats">
          {range && <span className="jr-tool-card-verso-range">{range}</span>}
          {evolved ? (
            <span className="jr-tool-card-verso-xp" data-evolved>
              <span className="jr-tool-card-verso-xp-label">✦ ÉVOLUÉ</span>
            </span>
          ) : (
            <span
              className="jr-tool-card-verso-xp"
              aria-label={`${formatCardXp(xpMax)} HORA maximum`}
            >
              <span className="jr-tool-card-verso-xp-label">HORA</span>
              <span className="jr-tool-card-verso-xp-value" aria-hidden>
                {cardXpGroups(xpMax).map((g, i) => (
                  <span key={i} className="jr-tool-card-verso-xp-group">
                    {g}
                  </span>
                ))}
              </span>
            </span>
          )}
          <span className="jr-tool-card-verso-tags">
            <span className="jr-tool-card-verso-chip">
              {cardHashtag(CARD_TYPE_LABELS[cardType] ?? cardType)}
            </span>
            {domainLabel && (
              <span className="jr-tool-card-verso-chip">{cardHashtag(domainLabel)}</span>
            )}
          </span>
        </span>
      </header>

      <h2 className="jr-tool-card-verso-title" data-size={versoTitleSize(stripMath(title))}>
        <MathText>{title}</MathText>
      </h2>

      {emblem && (
        <div className="jr-tool-card-verso-emblem" data-kind={emblem.kind}>
          {emblem.kind === 'formula' ? (
            <FitWidth>
              <MarkdownMath>{`$$${emblem.latex}$$`}</MarkdownMath>
            </FitWidth>
          ) : (
            <MarkdownMath>{emblem.md}</MarkdownMath>
          )}
        </div>
      )}

      <div className="jr-tool-card-qr-tile">
        <QrCodeImage
          value={buildCardQrUrl(shortCode)}
          size={480}
          margin={CARD_QR_OPTIONS.margin}
          errorCorrectionLevel={CARD_QR_OPTIONS.errorCorrectionLevel}
          color={CARD_QR_OPTIONS.color}
          alt="QR code vers la carte en ligne"
          className="jr-tool-card-qr"
        />
      </div>

      <div className="jr-tool-card-verso-links">
        <span className="jr-tool-card-verso-nav">Règle · Cours · Exercices</span>
        <span className="jr-tool-card-verso-url">{buildCardShortUrl(shortCode)}</span>
      </div>

      <footer className="jr-tool-card-verso-foot">
        <span className="jr-tool-card-verso-brand">
          <CompassLogo size="sm" showWordmark={false} animated={false} />
          <span className="jr-tool-card-verso-name">JeRaisonne</span>
        </span>
        <span className="jr-tool-card-verso-rarity" aria-label={`Rareté : ${rarityLabel}`}>
          <span className="jr-tool-card-verso-stars" aria-hidden>
            {'★'.repeat(stars)}
            <span className="jr-tool-card-verso-stars-off">{'★'.repeat(5 - stars)}</span>
          </span>
          <span className="jr-tool-card-verso-rarity-label" aria-hidden>
            {rarityLabel}
          </span>
        </span>
      </footer>
    </article>
  );
}

/** Largeur du gabarit de carte en px CSS (74 mm à 96 dpi ; rapport 63:88). */
export const CARD_WIDTH_PX = (74 / 25.4) * 96;

/**
 * Affiche une face à taille réelle, mise à l'échelle pour remplir la largeur
 * de son conteneur (aperçu éditeur, page publique).
 */
export function ScaledCard({
  children,
  className,
  autoHeight = false,
}: {
  children: ReactNode;
  className?: string;
  /** Hauteur = celle du contenu mis à l'échelle (carte allongée), au lieu du format 63:88. */
  autoHeight?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const next = el.clientWidth > 0 ? el.clientWidth / CARD_WIDTH_PX : 1;
      setScale(next);
      if (autoHeight && innerRef.current) setHeight(innerRef.current.offsetHeight * next);
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (autoHeight && innerRef.current) ro.observe(innerRef.current);
    return () => ro.disconnect();
  }, [autoHeight]);

  return (
    <div
      ref={ref}
      className={`jr-tool-card-scaler ${className ?? ''}`}
      data-auto-height={autoHeight || undefined}
      style={autoHeight && height != null ? { height } : undefined}
    >
      <div
        ref={innerRef}
        className="jr-tool-card-scaled"
        style={{ '--jr-tool-card-scale': scale } as CSSProperties}
      >
        {children}
      </div>
    </div>
  );
}
